/**
 * Projects are the unit of work the rest of the site hangs off: the hero rail
 * lists them, each Focus Area page highlights its own, and every story, article,
 * report and gallery item can be attributed to one.
 *
 * `program` matches a Focus Area slug, which is what the filter tabs switch on.
 */

import { ARTICLES, STORIES, DOWNLOADS, GALLERY, DOC_COLLECTIONS, type DocCollection } from './resources';
import type { FocusAttachments } from './focusContent';
import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';

import harmlistLogo from '@podus/ui/logo-icons/harmlist.png';
import irisLogo from '@podus/ui/logo-icons/iris.png';
import justiceClockLogo from '@podus/ui/logo-icons/justice-clock.png';
import openJusticeLogo from '@podus/ui/logo-icons/open-justice.png';
import podusAidLogo from '@podus/ui/logo-icons/podus-aid.png';
import podusAiLogo from '@podus/ui/logo-icons/podus-ai.png';
import podusAppointmentLogo from '@podus/ui/logo-icons/podus-appointment.png';
import podusContractLogo from '@podus/ui/logo-icons/podus-contract.png';
import podusMultidoorLogo from '@podus/ui/logo-icons/podus-multidoor.png';
import yourcheckaLogo from '@podus/ui/logo-icons/yourcheaka.png';

export type ProjectStatus = 'Active' | 'Pilot' | 'Completed';

export interface ProjectStat {
    value: string;
    label: string;
}

export interface Project {
    slug: string;
    name: string;
    /** One line, used on the card and under the detail hero. */
    tagline: string;
    /**
     * Focus Area slugs this project sits under. A project can serve more than one
     * programme — a duty-solicitor scheme is both access to justice and judicial
     * accountability — so this is a list, and the first entry is the primary one
     * used for the breadcrumb.
     */
    programs: string[];
    status: ProjectStatus;
    /** Position in the homepage hero rail. 0 = not in it. */
    heroSlot: number;
    /**
     * Position in each programme page's highlighted rail, keyed by slug.
     * A missing or 0 entry means it is not on that page's rail.
     */
    programSlots: Record<string, number>;
    image: string;
    /** Product mark, from `@podus/ui/logo-icons`. Shown on the card and detail hero. */
    logo: string;
    /** Where the product itself lives, if it has a public home. */
    website?: string;
    started: string;
    /** Where it runs, shown in the at-a-glance strip. */
    coverage: string;
    /** Headline numbers on the detail page. */
    stats: ProjectStat[];
    /** "The Problem" — what was wrong before this existed. */
    problem: string[];
    /** "Our Solution" — what the project actually does about it. */
    solution: string[];
    /** The commitments the project is measured against. */
    objectives: string[];
    /** Bundled sample relations, as indices into the pools — the offline fallback. */
    storyIndices: number[];
    articleIndices: number[];
    galleryIndices: number[];
    documentIndices: number[];
    /** Set once the CMS has answered; from then on it alone decides what the page shows. */
    attached?: FocusAttachments;
}

export const PROGRAMS: { slug: string; label: string }[] = [
    { slug: 'tech-for-justice', label: 'Tech For Justice' },
    { slug: 'access-to-justice', label: 'Access To Justice' },
    { slug: 'judicial-accountability', label: 'Judicial Accountability' },
    { slug: 'democratic-governance', label: 'Democratic Governance' },
    { slug: 'sgbv', label: 'SGBV' },
];

export const programLabel = (slug: string): string => PROGRAMS.find((p) => p.slug === slug)?.label ?? slug;

/**
 * Every project the site knows. The CMS owns this list once it has answered — a
 * published project is added, a drafted one removed — so the bundled ten are
 * the offline fallback, not the ceiling.
 */
export const PROJECTS: Project[] = [
    {
        slug: 'podus-ai',
        name: 'Podus AI',
        tagline: 'Ask a legal question in your own words, on WhatsApp, and get an answer grounded in Nigerian law.',
        programs: ['tech-for-justice', 'access-to-justice'],
        status: 'Active',
        heroSlot: 1,
        programSlots: { 'tech-for-justice': 1, 'access-to-justice': 3 },
        image: '/landing/innovations/contract-card.webp',
        logo: podusAiLogo,
        website: 'https://podus.ai',
        started: 'March 2024',
        coverage: 'Nigeria, with expansion under way to Ghana and Senegal',
        stats: [
            { value: '33,000+', label: 'Users onboarded' },
            { value: '4', label: 'Ways to reach it' },
            { value: 'Free', label: 'Cost to ask' },
        ],
        problem: [
            'Most legal questions in Nigeria never reach a lawyer. They are asked at a counter that has closed, to a relative who is guessing, or not at all — and the cost of guessing wrong arrives months later, usually as a signature already given.',
            'The barrier is rarely the law itself. It is that finding out what the law says costs a consultation fee, a trip across town, and a working day most people cannot give up.',
        ],
        solution: [
            'Podus AI is trained on Nigerian statutes and judicial precedent. A user describes their situation by typing or speaking on WhatsApp; the assistant explains the position in plain language, cites the law it is relying on, and names the agency or court the matter should go to.',
            'Where a document is needed, it drafts one. Where a lawyer is needed, it hands the matter to Podus Appointment or Podus Legal Aid with the context already gathered, rather than returning a phone number.',
            'It runs on WhatsApp, the web at chat.podus.ai, and the iOS and Android apps — deliberately including the channel people already have open, rather than one they must be persuaded to install.',
        ],
        objectives: [
            'Answer a first legal question without a fee, an appointment or a trip',
            'Cite the statute or authority behind every substantive answer',
            'Hand over to a human lawyer the moment the question needs one',
        ],
        storyIndices: [3],
        articleIndices: [3, 4, 5],
        galleryIndices: [2, 7],
        documentIndices: [10, 7],
    },
    {
        slug: 'justice-clock',
        name: 'Justice Clock',
        tagline: 'The clock starts when a case is filed, and every desk that holds it up is on the record.',
        programs: ['judicial-accountability', 'tech-for-justice'],
        status: 'Active',
        heroSlot: 2,
        programSlots: { 'judicial-accountability': 1, 'tech-for-justice': 3 },
        image: '/landing/justice-performance/skyline.webp',
        logo: justiceClockLogo,
        started: 'June 2021',
        coverage: 'Lagos and Ogun States',
        stats: [
            { value: '2', label: 'State justice ministries' },
            { value: '4', label: 'Justice actors linked' },
            { value: '2020', label: 'Case files loaded from' },
        ],
        problem: [
            'A file that stops moving inside the Directorate of Public Prosecutions stops moving silently. Complainants told us the same thing for years: the case had gone in for legal advice and nobody could say when, or whether, it would come back out.',
            'That silence is what keeps people on remand past the limits the Administration of Criminal Justice Law sets. Delay with no owner and no timestamp is not a decision anyone can appeal — it is a decision never reached.',
        ],
        solution: [
            'Justice Clock is an electronic case-tracking platform built with the Lagos State Ministry of Justice, with support from the Open Society Initiative for West Africa, and deployed since with Ogun State. It puts the Judiciary, the Ministry of Justice, the Police and the correctional centres on one record of the same file.',
            'Litigants, lawyers, magistrates and judges can see where a file physically is and whether legal advice has been issued, and can apply for a certified copy online instead of walking it through the registry.',
            'The system tracks matters in progress and prompts the responsible desk officer when one falls into default — which is what turns a tracker into something that actually moves files.',
        ],
        objectives: [
            'Give every complainant a way to see where their file is without asking a clerk',
            'Prompt the responsible desk officer before a case defaults, not after',
            'Hold the DPP to the constitutional time limits on awaiting-trial detention',
        ],
        storyIndices: [1, 2],
        articleIndices: [1, 2, 10],
        galleryIndices: [0, 5],
        documentIndices: [0, 11, 5],
    },
    {
        slug: 'podus-legal-aid',
        name: 'Podus Legal Aid',
        tagline: 'Describe what happened, and a pro bono lawyer near you picks it up.',
        programs: ['access-to-justice'],
        status: 'Active',
        heroSlot: 3,
        programSlots: { 'access-to-justice': 1 },
        image: '/landing/legal-aid/gemini.webp',
        logo: podusAidLogo,
        website: 'https://app.podus.org',
        started: '2017',
        coverage: '19 states of Nigeria, from offices in Ibadan, Lagos and Abuja',
        stats: [
            { value: '6,500+', label: 'Legal interventions' },
            { value: '150+', label: 'Pro bono lawyers' },
            { value: '19', label: 'States covered' },
        ],
        problem: [
            'Free representation is worthless if the person entitled to it never hears that it exists. Capacity figures for legal aid measure supply and say nothing about whether the supply reaches anyone.',
            'The people who most need it — detainees, survivors of police brutality, tenants, consumers stripped by a lender — are also the least able to spend a week finding out who is obliged to help them.',
        ],
        solution: [
            'Podus Legal Aid works the way a ride-hailing app works. A person logs the matter, and a volunteer lawyer within reach of them is alerted and takes it on.',
            'It is the arm of Citizens’ Gavel that has been running longest, and it carries the ordinary caseload: police brutality, consumer protection, landlord and tenant disputes, and straightforward legal advisory. During the #EndSARS protests in 2020 the same network mobilised around 250 volunteer lawyers and handled more than 400 intervention cases.',
            'Matters are run to conclusion rather than triaged and released, which is why the count we publish is interventions completed, not enquiries received.',
        ],
        objectives: [
            'Put a named lawyer on a matter, not a phone number in front of a claimant',
            'Keep the service free at the point of need for anyone who qualifies',
            'Report interventions completed rather than enquiries received',
        ],
        storyIndices: [0, 3],
        articleIndices: [0, 3],
        galleryIndices: [4, 1],
        documentIndices: [7, 10, 2],
    },
    {
        slug: 'iris',
        name: 'IRIS',
        tagline: 'Intimate Relationship Indicators Screening — spotting the pattern before it becomes an injury.',
        programs: ['sgbv', 'tech-for-justice'],
        status: 'Active',
        heroSlot: 4,
        programSlots: { 'sgbv': 1, 'tech-for-justice': 4 },
        image: '/landing/stories/story2.webp',
        logo: irisLogo,
        started: 'December 2025',
        coverage: 'Nationwide, through partner churches and accredited counsellors',
        stats: [
            { value: '3', label: 'Audiences served' },
            { value: '4', label: 'Behavioural domains' },
            { value: '100%', label: 'Digital and private' },
        ],
        problem: [
            'The response to intimate partner violence in Nigeria is almost entirely a crisis response. It begins at the hospital, the station or the shelter — which is to say, after the harm.',
            'The patterns that precede that harm are visible much earlier, and are routinely read as ordinary friction. Victims stay for reasons that have nothing to do with not noticing: emotional dependence, money, family pressure, and the certainty of being blamed for saying anything.',
        ],
        solution: [
            'IRIS is a digital behavioural assessment that asks subtle, research-backed questions about emotional regulation, communication style, conflict management and other relationship dynamics, and returns a picture of where a relationship is under strain.',
            'Every flagged area comes with a tailored, scripture-based learning module rather than a verdict. It was built with marriage counsellors, certified behavioural and clinical psychologists, and experienced faith leaders, and is designed to be customised into the premarital counselling programme a church already runs.',
            'It serves couples in premarital counselling, married couples, and individuals working on their own readiness. Being fully digital is the point: it can be taken privately, by someone who is not ready to say anything out loud.',
        ],
        objectives: [
            'Move the response to IPV from crisis intervention to early detection',
            'Return a growth-focused module for every concern raised, never a label',
            'Fit inside the counselling programmes churches already run, rather than replacing them',
        ],
        storyIndices: [2],
        articleIndices: [8, 2],
        galleryIndices: [3, 6],
        documentIndices: [8, 4],
    },
    {
        slug: 'open-justice',
        name: 'Open Justice',
        tagline: 'The research, petitions and rankings that make judicial delay a fact on the record.',
        programs: ['judicial-accountability', 'democratic-governance'],
        status: 'Active',
        heroSlot: 5,
        programSlots: { 'judicial-accountability': 2, 'democratic-governance': 1 },
        image: '/landing/report/cover-main.webp',
        logo: openJusticeLogo,
        website: 'https://open-justice.gavel.ng',
        started: '2017',
        coverage: 'Nationwide, across the state high courts',
        stats: [
            { value: '4', label: 'Alliance partners' },
            { value: '3', label: 'Lines of work' },
            { value: 'Open', label: 'Everything published' },
        ],
        problem: [
            'Every registry records the same events in a different shape, so figures that ought to be comparable are not. Two courts can both be right about their disposal rate and mean entirely different things by it.',
            'That makes a national picture impossible to assemble honestly, and hands any court that dislikes a finding a reasonable objection to it. Complaints about a slow court could always be answered with an anecdote about a fast one, and both were unfalsifiable.',
        ],
        solution: [
            'Open Justice is the name Citizens’ Gavel started under in 2017, and it is still the accountability arm: research into how the judiciary actually performs, legal petitions where the finding demands one, and infographics that put the result in front of people who do not read reports.',
            'It now runs as the Open Justice Alliance, alongside Hope Behind Bars, TapNitiative and the Network of University Legal Aid Institutions — four organisations pooling evidence rather than publishing four partial pictures of the same system.',
            'The recurring publication is Measuring The Pace Of Justice, which ranks state high courts on how long they take. Ranking is the part that travels: a court that objects to its position has to argue about the numbers.',
        ],
        objectives: [
            'Publish court performance data openly, in a form other organisations can reuse',
            'Turn every finding that warrants it into a petition with a named respondent',
            'Rank rather than describe, so that improvement is visible year on year',
        ],
        storyIndices: [2],
        articleIndices: [1, 9],
        galleryIndices: [5, 0],
        documentIndices: [9, 11, 0],
    },
    {
        slug: 'podus-multidoor',
        name: 'Podus Multidoor Court',
        tagline: 'File, track and settle a dispute through the Multi-Door Courthouse without entering a courtroom.',
        programs: ['access-to-justice', 'tech-for-justice'],
        status: 'Active',
        heroSlot: 6,
        programSlots: { 'access-to-justice': 2, 'tech-for-justice': 2 },
        image: '/landing/programs/program-photo.webp',
        logo: podusMultidoorLogo,
        website: 'https://multidoor.podus.ai',
        started: '2025',
        coverage: 'Nigeria, through the Multi-Door Courthouse system',
        stats: [
            { value: '3', label: 'Resolution doors' },
            { value: '1', label: 'Account across Podus' },
            { value: 'Enforceable', label: 'Outcome' },
        ],
        problem: [
            'Not every dispute belongs in front of a judge, and most of the ones that end up there do not need to be. A tenancy disagreement or an unpaid invoice can sit on a cause list for years, costing both sides more than the matter is worth.',
            'Nigeria already has an alternative — the Multi-Door Courthouse — and most people with a dispute have never heard of it, or do not know how a case gets in front of one.',
        ],
        solution: [
            'Podus Multidoor connects a user directly to the Multi-Door Courthouse system and manages the case from there: mediation, arbitration, conciliation and the other court-recognised routes, each with an outcome the courts will enforce.',
            'A matter can be opened without leaving the conversation it came up in. Someone describing a dispute to Podus AI can initiate the case then and there, rather than being told which office to visit.',
            'It is the same Podus account used for guidance, appointments and contracts, so the history of the dispute is already attached when the neutral picks it up.',
        ],
        objectives: [
            'Open a case inside the conversation the dispute was described in',
            'Route every matter to the door that fits it, not to the most familiar one',
            'End with an outcome both sides can enforce',
        ],
        storyIndices: [3, 0],
        articleIndices: [4, 6],
        galleryIndices: [4, 8],
        documentIndices: [7, 10],
    },
    {
        slug: 'yourchecka',
        name: 'YourChecka',
        tagline: 'Background verification done on foot, funding legal aid with 40% of what it earns.',
        programs: ['tech-for-justice', 'access-to-justice'],
        status: 'Active',
        heroSlot: 7,
        programSlots: { 'tech-for-justice': 5 },
        image: '/landing/legal-aid/card-wide.webp',
        logo: yourcheckaLogo,
        website: 'https://yourchecka.com',
        started: '2026',
        coverage: 'Nigeria, with field agents on the ground',
        stats: [
            { value: '10', label: 'Verification stages' },
            { value: '3', label: 'Service tiers' },
            { value: '40%', label: 'Of profit to legal aid' },
        ],
        problem: [
            'The greatest exposure a household or a business has is to the people closest to it, because they hold the most information. A nanny, an executive assistant, a finance lead — the hire with access is the hire worth checking.',
            'Automated screening in Nigeria produces a false sense of security. There is not enough centralised data behind it to support a robust check, so a clean automated report frequently means nothing was found rather than nothing is there.',
        ],
        solution: [
            'YourChecka combines digital verification with human field intelligence. Agents go to the address, speak to landlords and neighbours, and run the community enquiries that a database cannot: identity, criminal record, guarantors, address history and previous workplaces, with academic and health verification available on top.',
            'The process runs across ten stages, from account creation to the final report, in three tiers — Basic, Standard and Professional. Standard adds social media checks and a risk score.',
            'It is a commercial product with a stated cross-subsidy: 40% of all profit funds legal aid for indigent Nigerians. Built under Podus+ Limited, it is the arm designed to pay for the arm that gives its work away.',
        ],
        objectives: [
            'Verify in person what a database cannot confirm',
            'Return a report the client can act on, not a score with no evidence behind it',
            'Send 40% of profit to legal aid for Nigerians who cannot pay for counsel',
        ],
        storyIndices: [0],
        articleIndices: [5, 9],
        galleryIndices: [2, 7],
        documentIndices: [9, 6],
    },
    {
        slug: 'podus-contract',
        name: 'Podus Contract',
        tagline: 'Draft, review and sign an agreement you can actually read.',
        programs: ['tech-for-justice', 'access-to-justice'],
        status: 'Active',
        heroSlot: 0,
        programSlots: { 'tech-for-justice': 6 },
        image: '/landing/innovations/aid-photo.webp',
        logo: podusContractLogo,
        website: 'https://contract.podus.ai',
        started: '2025',
        coverage: 'Nationwide',
        stats: [
            { value: 'Plain', label: 'Language on every clause' },
            { value: 'Digital', label: 'Signing' },
            { value: 'Tracked', label: 'Obligations and dates' },
        ],
        problem: [
            'Most agreements people sign in Nigeria were drafted by the party they are signing with. The document is not neutral, and the person with the most to lose is usually the only one in the room without a lawyer.',
            'The cost of not understanding a clause is never paid at signature. It is paid at renewal, at termination, or the first time the other side has a reason to enforce it.',
        ],
        solution: [
            'Podus Contract lets a user draft from vetted templates, review a document they have been handed, and get each clause explained in language that does not require a law degree — before the signature, which is the only point at which the explanation is worth anything.',
            'Agreements are signed digitally and then tracked, so obligations and dates surface as reminders rather than as a surprise. It shares the single Podus account, so a contract that turns into a dispute can go straight to Podus Multidoor.',
        ],
        objectives: [
            'Explain every clause before signature, not after a dispute',
            'Draft only from templates that have been vetted',
            'Surface each obligation and deadline as it falls due',
        ],
        storyIndices: [3],
        articleIndices: [4, 5],
        galleryIndices: [2, 7],
        documentIndices: [10, 7],
    },
    {
        slug: 'podus-appointment',
        name: 'Podus Appointment',
        tagline: 'Book a verified lawyer, see the fee first, pick a time that suits you.',
        programs: ['access-to-justice', 'tech-for-justice'],
        status: 'Active',
        heroSlot: 0,
        programSlots: { 'access-to-justice': 4 },
        image: '/landing/legal-aid/event.jpg',
        logo: podusAppointmentLogo,
        website: 'https://appointment.podus.ai',
        started: '2025',
        coverage: 'Nationwide',
        stats: [
            { value: 'Verified', label: 'Every professional listed' },
            { value: 'Upfront', label: 'Fees' },
            { value: 'Same day', label: 'Slots available' },
        ],
        problem: [
            'Finding a lawyer in Nigeria still runs on personal introduction. Without one, a person has no way to tell whether the name they have been given is on the roll, practises in the right area, or charges an amount they can meet.',
            'The fee is the part nobody will state in advance, which means the first consultation is entered blind — and is exactly the moment a person is least able to walk away from a number they cannot afford.',
        ],
        solution: [
            'Podus Appointment lists verified legal professionals by location and practice area, with the fee shown before the booking rather than after it, and a calendar the client picks from.',
            'It is the escalation route out of Podus AI. A question the assistant cannot responsibly answer becomes a booked consultation with the matter already written up, so the paid hour starts with the facts rather than with the background.',
            'Clients who qualify for free representation are routed to Podus Legal Aid instead, rather than being quoted a fee they were never going to pay.',
        ],
        objectives: [
            'Verify every professional listed before they can take a booking',
            'Show the fee before the booking, not at the consultation',
            'Route anyone who qualifies for free representation to Podus Legal Aid',
        ],
        storyIndices: [0, 3],
        articleIndices: [0, 3],
        galleryIndices: [4, 1],
        documentIndices: [7, 2],
    },
    {
        slug: 'harmlist',
        name: 'Harmlist',
        tagline: 'A public record of the loan apps and tech companies flagged for harming their customers.',
        programs: ['democratic-governance', 'tech-for-justice'],
        status: 'Active',
        heroSlot: 0,
        programSlots: { 'democratic-governance': 2, 'tech-for-justice': 7 },
        image: '/landing/problem/secondary-image.webp',
        logo: harmlistLogo,
        website: 'https://harmlist.com',
        started: '2025',
        coverage: 'Nigeria, focused on lending and consumer tech',
        stats: [
            { value: 'Search', label: 'Before you borrow' },
            { value: 'Verified', label: 'Reports only' },
            { value: 'Public', label: 'Every listing' },
        ],
        problem: [
            'Predatory digital lenders in Nigeria do the same things to different people in sequence: unauthorised access to a contact list, threats sent to an employer, defamation of a borrower who has already paid. Each victim experiences it as a private humiliation.',
            'That isolation is the business model. The next borrower has no way of knowing the app has done this before, because nobody has ever been able to see the pattern in one place.',
        ],
        solution: [
            'Harmlist is a searchable public record of companies flagged for unethical practice. Enter the name of a loan app or a tech company and see whether it has been reported, and what for.',
            'Listings come from user reports that we verify, combined with our own research — which is what keeps it a record rather than a complaints board. Anyone who has been through it can submit a report and warn the person coming next.',
            'It sits alongside the consumer protection cases Podus Legal Aid has always carried. Publishing the pattern is what turns an individual complaint into something a regulator can be asked to act on.',
        ],
        objectives: [
            'Verify every report before it is published against a named company',
            'Let anyone check a lender or a platform before money changes hands',
            'Turn repeated individual complaints into evidence a regulator can use',
        ],
        storyIndices: [0, 1],
        articleIndices: [6, 4],
        galleryIndices: [4, 8],
        documentIndices: [7, 10],
    },
];

export const getProject = (slug?: string): Project | undefined => PROJECTS.find((p) => p.slug === slug);

/** Projects belonging to one Focus Area, in listing order. */
export const projectsInProgram = (program: string): Project[] => PROJECTS.filter((p) => p.programs.includes(program));

/* ── Relation helpers ─────────────────────────────────────────────────── */

const pick = <T>(pool: T[], indices: number[]): T[] =>
    pool.length ? indices.map((i) => pool[i % pool.length]).filter((v, i, all) => all.indexOf(v) === i) : [];

export const projectStories = (p: Project): ArticleItem[] => p.attached?.stories ?? pick(STORIES, p.storyIndices);
export const projectArticles = (p: Project): ArticleItem[] => p.attached?.articles ?? pick(ARTICLES, p.articleIndices);
export const projectGallery = (p: Project): GalleryItem[] => p.attached?.gallery ?? pick(GALLERY, p.galleryIndices);

/** Documents per collection; offline the flat sample list is sliced across the three tabs. */
export const projectDocuments = (p: Project): Record<DocCollection, DownloadItem[]> => {
    if (p.attached) return p.attached.documents;
    const docs = pick(DOWNLOADS, p.documentIndices);
    const perTab = Math.max(1, Math.ceil(docs.length / DOC_COLLECTIONS.length));
    return Object.fromEntries(
        DOC_COLLECTIONS.map((collection, i) => [collection, docs.slice(i * perTab, i * perTab + perTab)]),
    ) as Record<DocCollection, DownloadItem[]>;
};

/* ── Featured rails ───────────────────────────────────────────────────── */

/** The homepage hero rail, in slot order. The CMS owns this ordering. */
export const HERO_PROJECTS: Project[] = PROJECTS.filter((p) => p.heroSlot > 0).sort((a, b) => a.heroSlot - b.heroSlot);

/** The highlighted rail on one Focus Area page, in that page's own slot order. */
export const featuredInProgram = (program: string): Project[] =>
    PROJECTS.filter((p) => (p.programSlots[program] ?? 0) > 0).sort(
        (a, b) => a.programSlots[program] - b.programSlots[program],
    );

/** The primary programme, used where only one can be shown. */
export const primaryProgram = (p: Project): string => p.programs[0] ?? '';
